import React from 'react';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';

import useReports from '@API/reports/useReports';
import { ListItem, SearchableList, Tooltip } from '@ds';
import { DashboardView } from '@experiment-management-shared/types';
import Popover from '@material-ui/core/Popover';
import { Report } from '@reports/types';
import { REPORT_EDIT_MODES } from '@routes/constants/reports';
import { reportRoute } from '@routes/utils/reports';
import styles from './AddToReportPopover.module.scss';

const AddToReportPopover = ({
  anchorEl,
  dashboard,
  onClose
}: {
  anchorEl: Element | null;
  dashboard: DashboardView;
  onClose: VoidFunction;
}) => {
  const { projectName, workspace } = useParams<{
    projectName: string;
    workspace: string;
  }>();
  const { data: reports = [], isLoading: isReportsLoading } = useReports();

  if (isReportsLoading) {
    return null;
  }

  const payload = { dashboard, name: dashboard.templateName };

  const renderAddToReportItem = (report: Report) => {
    const { isTemplate, reportName } = report;

    const editURL = reportRoute({
      editMode: REPORT_EDIT_MODES.EDIT,
      isTemplate,
      projectName,
      reportName,
      workspaceName: workspace
    });

    return (
      <Link
        className={styles.reportListLink}
        to={{ pathname: editURL, state: payload }}
      >
        <ListItem>
          <Tooltip className="truncated-string" content={reportName}>
            <>{reportName}</>
          </Tooltip>
        </ListItem>
      </Link>
    );
  };

  const newReportURL = reportRoute({
    editMode: REPORT_EDIT_MODES.EDIT,
    isTemplate: false,
    projectName,
    workspaceName: workspace
  });

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      onClose={onClose}
      open={Boolean(anchorEl)}
      PaperProps={{
        className: styles.popoverContainer
      }}
      elevation={0}
      transformOrigin={{
        vertical: -6,
        horizontal: 'right'
      }}
    >
      <SearchableList itemList={reports} searchField="reportName">
        <SearchableList.Result maxHeight={320}>
          {renderAddToReportItem}
        </SearchableList.Result>
        <SearchableList.NotFound />
        <SearchableList.Footer>
          <SearchableList.FooterActionLink
            // TODO: Daniel: Consider Legacy vs V2 UI design
            text="+ New report"
            to={{ pathname: newReportURL, state: payload }}
          />
        </SearchableList.Footer>
      </SearchableList>
    </Popover>
  );
};

AddToReportPopover.defaultProps = {
  anchorEl: null,
  dashboard: null
};

export default AddToReportPopover;
